import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ClockIcon as Clock,
  ChevronLeftIcon as ChevronLeft,
  ShareIcon as Share2,
  GlobeAltIcon as Globe,
  HashtagIcon as Hash,
  UserIcon,
} from "@heroicons/react/24/outline";
import blogApi from "@/api/blog";
import SEO from "@/components/ui/SEO";
import Loading from "@/components/ui/Loading";
import NotFoundPage from "./NotFoundPage";
import CommentSection from "@/components/blog/CommentSection";
import RelatedPosts from "@/components/blog/RelatedPosts";
import { useLanguage } from "@/context/LanguageContext";
import { formatDate, formatReadingTime, normalizeHtml } from "@/lib/utils";
import ShareButton from "@/components/blog/ShareButton";

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();

  const {
    data: post,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["blog", "post", slug, language],
    queryFn: () => blogApi.getPost(slug!, language),
    enabled: !!slug,
  });

  const { data: relatedPosts = [] } = useQuery({
    queryKey: ["blog", "post", slug, "related"],
    queryFn: () => blogApi.getRelatedPosts(slug!),
    enabled: !!slug,
  });

  if (isLoading) return <Loading fullScreen />;
  if (error || !post) return <NotFoundPage />;

  // --- Parse the Nested CMS Blocks ---
  let parsedContentArray: any[] = [];

  if (post.blocks && post.blocks.length > 0) {
    try {
      // 1. Get the content string from the first block
      const rawContent = post.blocks[0].content;

      // 2. Parse it into an object { html: "[{...}]" }
      const parsedOuter =
        typeof rawContent === "string" ? JSON.parse(rawContent) : rawContent;

      // 3. Extract the inner array string and parse it into an actual array
      const innerArrayString = parsedOuter.html || parsedOuter;
      parsedContentArray =
        typeof innerArrayString === "string"
          ? JSON.parse(innerArrayString)
          : innerArrayString;

      // 4. Ensure it's an array to prevent .map crashes
      if (!Array.isArray(parsedContentArray)) {
        parsedContentArray = [];
      }
    } catch (e) {
      console.error("Failed to parse blog blocks:", e);
    }
  }

  return (
    <>
      <SEO
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt}
        keywords={post.metaKeywords}
        canonical={post.canonicalUrl}
        ogImage={post.ogImage || post.featuredImageUrl}
        ogTitle={post.ogTitle}
        ogDescription={post.ogDescription}
        schemaMarkup={post.schemaMarkup}
      />

      <ScrollProgress />

      <article className="min-h-screen bg-slate-50 py-10 px-4 md:py-16">
        {/* Back Button */}
        <div className="max-w-4xl mx-auto mb-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-primary"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Blog
          </Link>
        </div>

        {/* Content Card */}
        <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-2xl overflow-hidden">
          {/* Hero Image */}
          {post.featuredImageUrl && (
            <div className="relative w-full h-[300px] sm:h-[400px]">
              <img
                src={post.featuredImageUrl}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 sm:p-12 lg:p-16 space-y-10">
            {/* Header Section */}
            <header className="space-y-6 text-center">
              {post.categoryName && (
                <Link
                  to={`/blog/categories/${post.categorySlug || post.categoryName.toLowerCase()}`}
                  className="inline-block px-3 py-1 rounded-full bg-indigo-50 text-primary hover:bg-indigo-100 transition-colors text-xs font-bold uppercase tracking-wider"
                >
                  {post.categoryName}
                </Link>
              )}

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight">
                {post.title}
              </h1>

              {/* Author & Meta */}
              <div className="flex flex-wrap items-center justify-center gap-6 pt-2">
                {post.author && (
                  <Link
                    to={`/blog/authors/${post.author.slug}`}
                    className="flex items-center gap-3 group"
                  >
                    <div className="h-10 w-10 rounded-full bg-slate-100 overflow-hidden relative border border-slate-200 group-hover:border-primary transition-colors">
                      {post.author.avatarUrl ? (
                        <img
                          src={post.author.avatarUrl}
                          alt={post.author.displayName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <UserIcon className="p-2 w-full h-full text-slate-400" />
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors">
                        {post.author.displayName}
                      </p>
                      {post.publishedAt && (
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          {formatDate(post.publishedAt, language)}
                        </p>
                      )}
                    </div>
                  </Link>
                )}

                {post.readingTimeMinutes > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-slate-500 pl-6 border-l border-slate-200">
                    <Clock className="h-4 w-4" />
                    <span>{formatReadingTime(post.readingTimeMinutes)}</span>
                  </div>
                )}
              </div>
            </header>

            <hr className="border-slate-100" />

            {/* Block Content (Using the newly parsed array) */}
            <div className="max-w-none mx-auto space-y-8 prose prose-lg prose-headings:font-bold prose-headings:text-slate-900 prose-a:text-primary">
              {parsedContentArray.map((block) => {
                if (block.type === "rich-text") {
                  return (
                    <div
                      key={block.id}
                      dangerouslySetInnerHTML={{
                        __html: normalizeHtml(block.content),
                      }}
                    />
                  );
                }

                if (block.type === "image") {
                  return (
                    <div key={block.id} className="flex flex-col gap-3">
                      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-slate-50">
                        <img
                          src={block.content}
                          alt={block.caption || "Content"}
                          className="absolute inset-0 w-full h-full object-contain"
                        />
                      </div>

                      {block.caption && (
                        <p className="text-center text-sm text-slate-500 italic">
                          {block.caption}
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-4">
                {post.tags.map((tag) => (
                  <Link
                    key={tag.id}
                    to={`/blog/tags/${tag.slug}`}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors border border-slate-100"
                  >
                    <Hash className="h-3.5 w-3.5 opacity-50" />
                    {tag.name}
                  </Link>
                ))}
              </div>
            )}

            <hr className="border-slate-100" />

            {/* Bottom Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
                  Share
                </span>
                <ShareButton icon={Share2} label="Copy Link" />
              </div>

              {post.author?.websiteUrl && (
                <div className="flex items-center gap-4 text-right">
                  <a
                    href={post.author.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-primary transition-colors"
                  >
                    <Globe className="h-5 w-5" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Sections */}
        <div className="max-w-4xl mx-auto mt-16 space-y-16">
          <RelatedPosts posts={relatedPosts} />
          <CommentSection postSlug={slug!} />
        </div>
      </article>
    </>
  );
};

export default BlogPostPage;

const ScrollProgress = () => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setWidth(progress);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 z-50 h-1 w-full bg-transparent">
      <div
        className="h-full bg-primary transition-all duration-150 ease-out"
        style={{ width: `${width}%` }}
      />
    </div>
  );
};
